import React, { useCallback, useMemo, useRef, useState } from 'react';
import style from './mbg_notif_price.module.css'
import { changeState, deleteAlertWithInput, formatCurrency, getAccMenu, isEmpty } from '../../../utils/Helper';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { useInfiniteQuery } from '@tanstack/react-query';
import FilterContainer from '../../../components/FilterContainer';
import { Box, Icon, Typography } from '@mui/material';
import { CButton } from '../../../components/StyledComponent';
import MaterialReactTable, { MRT_ShowHideColumnsButton } from 'material-react-table';
import TooltipIcon from '../../../components/TooltipIcon';
import { toast } from 'react-toastify';
import MBGNotifPriceApprove from './MBGNotifPriceApprove';
import Loading from '../../../components/Loading';
import { getMBGSupplierList } from '../../../controller/mbg/mbgSupplierController';
import { format, subDays } from 'date-fns';
import { getPrmStatus } from '../../../controller/globalController';
import { getPriceReqList, updatePriceReqStatus } from '../../../controller/mbg/mgbPriceReqController';
import ControlledDateRangePicker from '../../../components/controlled_component/ControlledDateRangePicker';
import ControlledTextField from '../../../components/controlled_component/ControlledTextField';
import ControlledAutoComplete from '../../../components/controlled_component/ControlledAutoComplete';

const defaultValues = async () => {
    const apiList = [getPrmStatus(), getMBGSupplierList({ q: { is_active: 1 } })]
    const [status, supplier] = await Promise.all(apiList)
    const date = { endDate: new Date(), startDate: subDays(new Date(), 7) }
    const statusList = status?.data ?? []
    const supplierList = supplier?.data ?? []
    return { sku_name: "", date, supplier: undefined, status: undefined, supplierList, statusList }
}
function MBGNotifPrice(props) {
    const access = getAccMenu()
    const { t } = useTranslation()
    const navigate = useNavigate()

    const tableContainerRef = useRef(null)

    const [state, setState] = useState({ toggle: false, data: undefined })
    const [sorting, setSorting] = useState([])
    const formMethod = useForm({ defaultValues: async () => await defaultValues() });
    const { control, handleSubmit, getValues } = formMethod

    const statusList = useWatch({ control, name: 'statusList', defaultValue: [] })
    const supplierList = useWatch({ control, name: 'supplierList', defaultValue: [] })

    const { data, fetchNextPage, isError, isFetching, isLoading, refetch } = useInfiniteQuery({
        queryKey: ["mbg_change_price_list", sorting],
        queryFn: async ({ pageParam = 1, signal }) => {
            const { sku_name, date, supplier, status: _status } = getValues()
            const start_date = isEmpty(date?.startDate) ? format(subDays(new Date(), 7), "yyyy-MM-dd") : format(date?.startDate, "yyyy-MM-dd")
            const end_date = isEmpty(date?.endDate) ? format(new Date(), "yyyy-MM-dd") : format(date?.endDate, "yyyy-MM-dd")

            const ob = sorting.reduce((obj, item) => ({ ...obj, [item.id]: item.desc ? 'DESC' : 'ASC' }), {})
            const param = { page: pageParam, start_date, end_date, ob }
            if (!isEmpty(sku_name)) param.sku_name = sku_name
            if (!isEmpty(_status)) param.status = _status?.id ?? null
            if (!isEmpty(supplier?.id)) param.company_id = supplier?.id ?? null

            const { data } = await getPriceReqList(param)
            const hasNextPage = data?.data?.length >= data?.per_page
            return { data: data?.data ?? [], limit: data?.per_page ?? 100, total: data?.total ?? 0, hasNextPage };
        },
        getNextPageParam: (lastPage, allPages) => lastPage.hasNextPage ? allPages.length + 1 : undefined,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    })

    const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data])
    const totalDBRowCount = data?.pages?.[0]?.total ?? 0
    const totalFetched = flatData.length

    // table
    const onOpenApprove = (data) => changeState(setState, { toggle: true, data })
    const onCloseApprove = () => changeState(setState, { toggle: false, data: undefined })
    const onSubmitApprove = () => { refetch(); changeState(setState, { toggle: false, data: undefined }) }

    const handleReject = async (_data) => {
        const { isConfirmed, value } = await deleteAlertWithInput({
            title: 'Apakah anda yakin untuk menolak perubahan harga ?',
            text: 'Masukan alasan penolakan'
        });
        if (!isConfirmed) return;

        const sentData = { id: _data?.id, status: 'reject', note: value }
        const { code } = await updatePriceReqStatus(sentData)

        const textMsg = 'menolak perubahan harga'
        const successFlag = code === 200
        if (!successFlag) return toast.error(`Gagal ${textMsg}`)
        toast.success(`Berhasil ${textMsg}`)
        refetch()
    }

    const columns = useMemo(() => [
        {
            accessorKey: 'created_at',
            header: t(`common.date`),
            maxSize: 100,
            muiTableBodyCellProps: {
                style: {
                    maxWidth: 100, // Maximum width of the cell
                    whiteSpace: 'normal', // Allows text wrapping
                    wordBreak: 'break-word', // Breaks words if necessary
                },
            },
            Cell: ({ cell }) => format(new Date(cell.getValue()), 'dd-MM-yyyy'),
        },
        {
            accessorKey: 'company_name',
            header: t('common.supplier'),
            maxSize: 100,
            muiTableBodyCellProps: {
                style: {
                    maxWidth: 100, // Maximum width of the cell
                    whiteSpace: 'normal', // Allows text wrapping
                    wordBreak: 'break-word', // Breaks words if necessary
                },
            },
        },
        {
            accessorKey: 'name',
            header: t('common.goods'),
            maxSize: 200,
            muiTableBodyCellProps: {
                style: {
                    maxWidth: 200, // Maximum width of the cell
                    whiteSpace: 'normal', // Allows text wrapping
                    wordBreak: 'break-word', // Breaks words if necessary
                },
            },
            Cell: ({ row }) => {
                const data = row.original
                return `${data?.sku} - ${data?.name}`
            },
        },
        {
            accessorKey: 'selling_price',
            header: t('harga jual'),
            maxSize: 90,
            Cell: ({ cell }) => formatCurrency(parseFloat(cell.getValue()))
        },
        {
            accessorKey: 'new_selling_price',
            header: t('harga jual baru'),
            maxSize: 90,
            Cell: ({ cell }) => formatCurrency(parseFloat(cell.getValue()))
        },
        {
            accessorKey: 'note',
            header: t('common.note'),
        },
        {
            accessorKey: 'status_name',
            header: t('common.status'),
            muiTableBodyCellProps: {
                style: {
                    whiteSpace: 'normal', // Allows text wrapping
                    wordBreak: 'break-word', // Breaks words if necessary
                },
            },
            enableResizing: false,
            Cell: ({ row }) => {
                const status = parseInt(row.original?.status ?? 0);
                const backgroundColor = status === 0 ? 'var(--primary_color)' : status === 2 ? "green" : "red"
                const status_style = { width: '100%', color: 'white', borderRadius: '5px', padding: '2px', backgroundColor }
                return (
                    <Typography variant="body1" sx={{ ...status_style, textWrap: 'wrap', wordBreak: 'break-word' }}>
                        {row.original?.status_name ?? "-"}
                    </Typography>
                )
            }
        },
        {
            accessorKey: 'id',
            header: 'Aksi',
            maxSize: 50,
            muiTableBodyCellProps: { sx: { placeItems: 'center' }, },
            Cell: ({ row }) => {
                const data = row.original;
                const statusFlag = ["0"].includes(String(data?.status))
                if (!statusFlag) return
                return (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '0.2rem', button: { minWidth: 0, padding: '0.1rem' } }}>
                        {/* {access?.update === "1" && ( */}
                        <CButton bg_color='green' onClick={() => onOpenApprove(data)}>
                            <TooltipIcon title={t('common.approve')} icon={'done'} />
                        </CButton>
                        {/* )} */}
                        {/* {access?.delete === "1" && ( */}
                        <CButton bg_color='red' onClick={() => handleReject(data)}>
                            <TooltipIcon title={t('common.reject')} icon={'do_not_disturb'} />
                        </CButton>
                        {/* )} */}
                    </Box>
                )
            }
        }
    ], [access])

    const fetchMoreOnBottomReached = useCallback((event) => {
        if (["", null, undefined].includes(event.target)) return;
        if (totalFetched >= totalDBRowCount || isFetching) return;
        const { scrollHeight, scrollTop, clientHeight } = event.target;
        if (scrollHeight - scrollTop - clientHeight < 10) { fetchNextPage(); }
    }, [fetchNextPage, isFetching, totalFetched, totalDBRowCount])
    // end table

    if (isLoading) return <Loading />
    return (
        <>
            {state.toggle && (
                <MBGNotifPriceApprove
                    open={state.toggle}
                    data={state.data}
                    onClose={onCloseApprove}
                    onSubmit={onSubmitApprove}
                />
            )}
            <FilterContainer>
                <Box component={"form"} onSubmit={handleSubmit(refetch)} className={style.form_container}>
                    <ControlledDateRangePicker control={control} name='date' />
                    <ControlledTextField control={control} name="sku_name" size="small" label={t(`master.goods.name`)} />
                    <ControlledAutoComplete control={control} name={"supplier"}
                        input_label={t(`common.supplier`)} options={supplierList}
                        isOptionEqualToValue={(option, value) => String(option?.id ?? "") === String(value?.id ?? "")}
                    />
                    <ControlledAutoComplete control={control} name={"status"}
                        input_label={t(`common.status`)} options={statusList}
                        isOptionEqualToValue={(option, value) => String(option?.id ?? "") === String(value?.id ?? "")}
                    />
                    <CButton type="submit" startIcon={<Icon>search</Icon>} sx={{ textTransform: "capitalize", gridColumn: "4" }}>
                        Cari
                    </CButton>
                </Box>
            </FilterContainer>
            <Box className={style.table_container}>
                <MaterialReactTable
                    columns={columns}
                    data={flatData}
                    enablePagination={false}
                    enableColumnActions={false}
                    enableColumnFilters={false}
                    enableStickyHeader
                    manualFiltering
                    manualSorting
                    enableRowNumbers={true}
                    rowNumberMode="static"
                    displayColumnDefOptions={{
                        "mrt-row-numbers": {
                            Header: () => t(`common.no`),
                            muiTableHeadCellProps: { align: "center", sx: { maxWidth: "fit-content" } },
                            muiTableBodyCellProps: { align: "center", sx: { maxWidth: "fit-content" } },
                        },
                    }}
                    muiTableHeadCellProps={{ align: "center", sx: { textTransform: "capitalize" } }}
                    muiTableBodyCellProps={{ align: "center" }}
                    muiTableBodyRowProps={{
                        align: "center",
                        hover: false,
                        sx: { "&:nth-of-type(even)": { backgroundColor: "var(--even_row_color) !important" } },
                    }}
                    renderToolbarInternalActions={({ table }) => (
                        <Box sx={{ display: "flex", gap: "0.5rem", "& .MuiIconButton-root": { padding: "0" } }}>
                            <MRT_ShowHideColumnsButton table={table} />
                        </Box>
                    )}
                    enablePinning={true}
                    enableColumnPinning={true}
                    enableColumnResizing={false}
                    initialState={{
                        density: "compact", columnPinning: {
                            left: ['mrt-row-numbers', 'created_at', 'company_name', 'name'],
                            right: ['status_name', 'id',]
                        }
                    }}
                    state={{
                        sorting,
                        isLoading: isLoading,
                        showAlertBanner: isError,
                        showProgressBars: isFetching,
                    }}
                    onSortingChange={setSorting}
                    muiTablePaperProps={{ elevation: 0 }}
                    muiTableContainerProps={{
                        ref: tableContainerRef,
                        sx: { maxHeight: "45dvh", height: '45dvh' },
                        className: "scroll_container",
                        onScroll: e => fetchMoreOnBottomReached(e),
                    }}
                    muiToolbarAlertBannerProps={{ sx: { display: "none" } }}
                    muiBottomToolbarProps={{ sx: { display: "none" } }}
                    muiTableHeadCellFilterTextFieldProps={{
                        sx: { width: "100%", "& .MuiInputBase-input": { padding: "var(--input_padding)" } },
                        variant: "outlined",
                    }}
                />
            </Box>
        </>
    );
}

export default MBGNotifPrice;